import humanizeDuration from 'humanize-duration';

import {
  type KeyOfType,
  getValueFromKeyOfType,
} from '../../../types/KeyOfType';
import { humanizeDurationUnitMeasures } from '../../../utils/humanizeDurationUnitMeasures';
import { createUptimeFilter } from '../../filters';
import { createDefaultFilter } from '../../filters/createDefaultFilter';
import type {
  TableColumnFormatLink,
  TableLinkColumnConfig,
} from '../../links/index';
import type { TableColumnBase } from '../TableColumnBase';
import {
  type TableDataColumnConfig,
  pickTableColumnConfig,
} from '../TableColumnConfig';
import { formatValueToCsvString } from './helpers/formatValueToCsvString';
import { mergeFieldDependencies } from './helpers/mergeFieldDependencies';

export type TableColumnFormatUptime<TRow> = {
  type: 'uptime';
  field: KeyOfType<TRow, number | null>;
} & TableColumnFormatLink<TRow>;

type UptimeColumnConfig<TRow, TDataType> = TableDataColumnConfig<
  TRow,
  TDataType
> &
  TableLinkColumnConfig<TRow>;

export const formatUptimeCellValue = (value: number | null): string | null =>
  value === null
    ? null
    : humanizeDuration(Math.round(value * 1000), {
        unitMeasures: humanizeDurationUnitMeasures,
      });

export const createUptimeColumn = <TRow>(
  config: UptimeColumnConfig<TRow, number | null> & { array?: false },
): TableColumnBase<TRow, TableColumnFormatUptime<TRow>> => ({
  ...pickTableColumnConfig(config),
  format: {
    type: 'uptime',
    field: config.key,
    createLinkProps: config.link?.createLinkProps,
  },
  csvStringDependencies: [config.key],
  webDependencies: mergeFieldDependencies(
    config.key,
    config.link?.dependencies,
  ),
  toCsvString: (row) =>
    formatValueToCsvString(
      getValueFromKeyOfType<TRow, number | null>(row, config.key),
      (v: number | null): string => formatUptimeCellValue(v) ?? '',
    ),
  filter: createDefaultFilter(config.filter, createUptimeFilter()),
});
